import React, {useEffect, useState} from 'react';
import './EditProfileModal.scss';
import {Flex, Form, Input, Modal} from "antd";
import { ReactComponent as CrossIcon } from '../../../../assets/images/global-icons/cross_ic.svg';
import UploadAvatar from "../../../../pages/SignUp/Personal/Profile/components/UploadAvatar/UploadAvatar";
import styles from "../../../../pages/SignUp/Personal/Profile/Profile.module.scss";
import TextArea from "antd/lib/input/TextArea";
import CustomButton from "../../../UI/CustomButton/CustomButton";
import {useForm} from "antd/lib/form/Form";
import {useUpdateProfileMutation} from "../../../../redux/API/userSlice";
import {useSelector} from "react-redux";
import {RootState} from "../../../../redux/store";
import {useUpdateCompanyMutation} from "../../../../redux/API/companySlice";

interface UserDataInterface {
  accountType: string,
  authType: string,
  avatarKey: string,
  company: any,
  createdAt: string,
  description: string,
  email: string,
  firstName: string,
  id: string,
  isBlocked: boolean,
  isDeleted: boolean,
  lastName: string,
  phone: string,
  updatedAt: string
}

interface EditProfileModalProps {
  visible: boolean;
  onClose: () => void;
  user: UserDataInterface | undefined;
  companyRefetch: Function;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({visible, onClose, user, companyRefetch}) => {
  const [avatar, setAvatar] = useState<string>('');
  const {isAuth} = useSelector((state: RootState) => state.isAuth);
  const [isChanged, setIsChanged] = useState(false);
  const [form] = useForm()
  const [initialValues, setInitialValues] = useState({
    firstName: '',
    lastName: '',
    about: '',
    avatar: '',
    description: '',
    companyName: ''
  })

  const [updateProfile] = useUpdateProfileMutation();
  const [updateCompany] = useUpdateCompanyMutation();

  useEffect(() => {
    const avatarToSet = user?.accountType === 'personal' ? user?.avatarKey : user?.company.picture;
    setAvatar(avatarToSet ?? '');

    if (user?.accountType === 'personal') {
      form.setFieldValue('firstName', user?.firstName);
      form.setFieldValue('lastName', user?.lastName);
    }

    form.setFieldValue('about', user?.accountType === 'personal' ? user?.description : user?.company.description);
    form.setFieldValue('avatar', user?.accountType === 'personal' ? user?.avatarKey : user?.company.picture);

    if (user?.accountType !== 'personal')
      form.setFieldValue('companyName', user?.company?.companyName);
  }, [user, visible]);

  const handleFieldsChange = () => {
    const currentValues = form.getFieldsValue();
    const hasChanges =
      currentValues.firstName !== initialValues.firstName ||
      currentValues.lastName !== initialValues.lastName ||
      currentValues.about !== initialValues.about ||
      avatar !== initialValues.avatar;

    setIsChanged(hasChanges);
  };

  const handleSave = async () => {
    try {
      if (user?.accountType === 'personal') {
        await updateProfile({
          id: user?.id || '',
          accessToken: isAuth,
          body: {
            firstName: form.getFieldValue('firstName') || '',
            lastName: form.getFieldValue('lastName') || '',
            avatarKey: avatar || '',
            description: form.getFieldValue('about') || ''
          }
        }).unwrap();
      } else {
        await updateCompany({
          id: user?.company.id || '',
          accessToken: isAuth,
          body: {
            companyName: form.getFieldValue('companyName') || '',
            picture: avatar || '',
            description: form.getFieldValue('about') || ''
          }
        }).unwrap();

        await companyRefetch()
      }

      onClose();
    } catch (error) {
      console.error(error);
    }
  }

  console.log(user)

  return (
    <Modal
      open={visible}
      footer={null}
      closable={false}
      maskClosable={true}
      onCancel={onClose}
      centered
      className={'EditProfileModal'}
    >
      <Flex vertical>
        <Flex justify="space-between" align="center" className={'EditProfileModal__header'}>
          <h3>Edit profile</h3>
          <CrossIcon color={"#000000"} onClick={onClose} />
        </Flex>
        <Form
          layout="vertical"
          className={'EditProfileModal__body__form'}
          form={form}
          initialValues={initialValues}
          onFieldsChange={handleFieldsChange}
          onFinish={handleSave}
        >
          <Form.Item
            name={"avatar"}
          >
            <Flex justify={"center"}>
              <UploadAvatar avatar={avatar} setAvatar={setAvatar}/>
            </Flex>
          </Form.Item>
          {
            user?.accountType === 'personal' ?
              <>
                  <Form.Item label="Your First Name"
                             name={"firstName"}
                             rules={[
                               { required: true, message: "This field is required" },
                               { min: 2, message: "Minimum 2 characters" },
                               { max: 20, message: "Maximum 20 characters" },
                               {
                                 pattern: /^[^\s][a-zA-Z0-9&“\-_. ]*$/,
                                 message: "Invalid characters or starts with whitespace"
                               },
                               {
                                 validator: (_, value) =>
                                   value && /\s{2,}/.test(value)
                                     ? Promise.reject("No double spaces allowed")
                                     : Promise.resolve()
                               }
                             ]}
                  >
                      <Input
                          placeholder="First name"
                          className={'EditProfileModal__body__form__input'}
                      />
                  </Form.Item>
                  <Form.Item label="Your Last Name"
                             name={"lastName"}
                             rules={[
                               { required: true, message: "This field is required" },
                               { min: 2, message: "Minimum 2 characters" },
                               { max: 20, message: "Maximum 20 characters" },
                               {
                                 pattern: /^[^\s][a-zA-Z0-9&“\-_. ]*$/,
                                 message: "Invalid characters or starts with whitespace"
                               },
                               {
                                 validator: (_, value) =>
                                   value && /\s{2,}/.test(value)
                                     ? Promise.reject("No double spaces allowed")
                                     : Promise.resolve()
                               }
                             ]}
                  >
                      <Input
                          placeholder="Last name"
                          className={'EditProfileModal__body__form__input'}
                      />
                  </Form.Item>
              </>
              :
              <>
                <Form.Item label="Company name"
                           name={"companyName"}
                           rules={[
                             { required: true, message: "This field is required" },
                             { min: 2, message: "Minimum 2 characters" },
                             { max: 50, message: "Maximum 50 characters" },
                             {
                               pattern: /^[^\s][a-zA-Z0-9&“"()\-_. @$\/\\]*$/, 
                               message: "Invalid characters or starts with whitespace"
                             },
                             {
                               validator: (_, value) =>
                                 value && /\s{2,}/.test(value)
                                   ? Promise.reject("No double spaces allowed")
                                   : Promise.resolve()
                             }
                           ]}
                >
                  <Input
                    placeholder="Company name"
                    className={'EditProfileModal__body__form__input'}
                  />
                </Form.Item>
              </>
          }
          <Form.Item label={user?.accountType === 'personal' ? "About me" : "Describe your business"}
                     name={"about"}
          >
            <TextArea
              autoSize={true}
              placeholder="Type something..."
              className={'EditProfileModal__body__form__textarea'}
            />
          </Form.Item>
          <Form.Item>
            <Flex className={'EditProfileModal__body__form__button'}>
              <CustomButton text="Save" disable={!isChanged} htmlType="submit" />
            </Flex>
          </Form.Item>
        </Form>
      </Flex>
    </Modal>
  );
};

export default EditProfileModal;